import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { PlayerAvatar } from "./PlayerAvatar";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Loader2, CalendarCheck, Scale, Plus, Minus, Trash2, UserX, ChevronDown } from "lucide-react";

export function ConductView({ isAdmin, matchdays }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [panelOpen, setPanelOpen] = useState(false);

  // fair-play form
  const [fpPlayer, setFpPlayer] = useState("");
  const [fpDelta, setFpDelta] = useState(3);
  const [fpReason, setFpReason] = useState("");
  const [saving, setSaving] = useState(false);

  // absence form
  const [absPlayer, setAbsPlayer] = useState("");
  const [absDay, setAbsDay] = useState("");

  const load = useCallback(async () => {
    const r = await api.get("/conduct");
    setData(r.data);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const rows = data?.rows || [];
  const attendance = [...rows].sort((a, b) => b.presences - a.presences || b.attendance_rate - a.attendance_rate);
  const fairplay = [...rows].sort((a, b) => b.fairplay_points - a.fairplay_points || a.name.localeCompare(b.name));
  const maxPres = Math.max(1, data?.total_days || 1);

  const submitFairplay = async () => {
    if (!fpPlayer) { toast.error("Seleziona un giocatore"); return; }
    setSaving(true);
    try {
      await api.post("/fairplay", { player_id: fpPlayer, delta: Number(fpDelta), reason: fpReason });
      toast.success("Punti fair-play assegnati");
      setFpReason("");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  const removeEntry = async (id) => {
    try { await api.delete(`/fairplay/${id}`); await load(); } catch { toast.error("Errore"); }
  };

  const submitAbsence = async (absent) => {
    if (!absPlayer || !absDay) { toast.error("Seleziona giocatore e giornata"); return; }
    try {
      await api.post("/absence", { player_id: absPlayer, matchday: Number(absDay), absent });
      toast.success(absent ? "Assenza registrata" : "Assenza rimossa");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  if (loading) {
    return <div className="py-16 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-emerald-400" /></div>;
  }

  const selectCls = "bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-100 focus:outline-none focus:ring-1 focus:ring-emerald-500";

  return (
    <div className="space-y-6" data-testid="conduct-view">
      <div>
        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-400">Comportamento</div>
        <h2 className="font-display font-extrabold uppercase tracking-tight text-3xl sm:text-4xl text-white">
          Presenze & Fair-Play
        </h2>
      </div>

      {isAdmin && (
        <div className="ap-card overflow-hidden">
          <button
            data-testid="conduct-admin-toggle"
            onClick={() => setPanelOpen((o) => !o)}
            className="w-full flex items-center justify-between px-5 py-3 text-left"
          >
            <span className="font-display uppercase tracking-wide text-sm text-emerald-400">Pannello Amministratore</span>
            <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${panelOpen ? "rotate-180" : ""}`} />
          </button>
          {panelOpen && (
            <div className="px-5 pb-5 grid md:grid-cols-2 gap-6 border-t border-white/5 pt-4">
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm font-semibold text-slate-200">
                  <Scale className="w-4 h-4 text-emerald-400" /> Assegna punti fair-play
                </div>
                <div className="flex flex-col gap-2">
                  <select data-testid="fp-player-select" className={selectCls} value={fpPlayer} onChange={(e) => setFpPlayer(e.target.value)}>
                    <option value="">Seleziona giocatore…</option>
                    {rows.map((r) => <option key={r.player_id} value={r.player_id}>{r.name}</option>)}
                  </select>
                  <div className="flex items-center gap-2">
                    <button type="button" onClick={() => setFpDelta((d) => Number(d) - 1)} className="w-9 h-9 rounded-lg bg-slate-800 text-slate-200 flex items-center justify-center hover:bg-slate-700"><Minus className="w-4 h-4" /></button>
                    <input data-testid="fp-delta-input" type="number" value={fpDelta} onChange={(e) => setFpDelta(e.target.value)} className={`${selectCls} w-20 text-center font-mono`} />
                    <button type="button" onClick={() => setFpDelta((d) => Number(d) + 1)} className="w-9 h-9 rounded-lg bg-slate-800 text-slate-200 flex items-center justify-center hover:bg-slate-700"><Plus className="w-4 h-4" /></button>
                    <span className="text-xs text-slate-500">punti (negativi = penalità)</span>
                  </div>
                  <input data-testid="fp-reason-input" value={fpReason} onChange={(e) => setFpReason(e.target.value)} placeholder="Motivo (facoltativo)" className={selectCls} />
                  <Button data-testid="fp-submit-btn" onClick={submitFairplay} disabled={saving} className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold uppercase text-xs tracking-wide">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Assegna"}
                  </Button>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm font-semibold text-slate-200">
                  <UserX className="w-4 h-4 text-rose-400" /> Gestisci assenze
                </div>
                <div className="flex flex-col gap-2">
                  <select data-testid="abs-player-select" className={selectCls} value={absPlayer} onChange={(e) => setAbsPlayer(e.target.value)}>
                    <option value="">Seleziona giocatore…</option>
                    {rows.map((r) => <option key={r.player_id} value={r.player_id}>{r.name}</option>)}
                  </select>
                  <select data-testid="abs-day-select" className={selectCls} value={absDay} onChange={(e) => setAbsDay(e.target.value)}>
                    <option value="">Seleziona giornata…</option>
                    {(matchdays || []).map((m) => <option key={m.number} value={m.number}>Giornata {m.number}</option>)}
                  </select>
                  <div className="flex gap-2">
                    <Button data-testid="abs-mark-btn" onClick={() => submitAbsence(true)} className="flex-1 bg-rose-500/90 hover:bg-rose-500 text-white font-bold uppercase text-xs tracking-wide">Segna assente</Button>
                    <Button data-testid="abs-clear-btn" onClick={() => submitAbsence(false)} variant="outline" className="flex-1 border-slate-700 text-slate-300 font-bold uppercase text-xs tracking-wide">Rimuovi</Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Presenze */}
        <div className="ap-card p-5" data-testid="attendance-section">
          <div className="flex items-center gap-2 mb-4">
            <CalendarCheck className="w-5 h-5 text-blue-400" />
            <h3 className="font-display uppercase tracking-wide text-lg text-white">Classifica Presenze</h3>
            <span className="ml-auto font-mono text-xs text-slate-500">{data?.total_days || 0} giornate giocate</span>
          </div>
          <div className="space-y-2">
            {attendance.map((r, i) => (
              <div key={r.player_id} data-testid={`attendance-row-${r.player_id}`} className="flex items-center gap-3">
                <span className="w-5 text-center font-mono text-xs text-slate-500">{i + 1}</span>
                <PlayerAvatar id={r.player_id} name={r.name} size={30} ring={false} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm text-slate-200 truncate">{r.name}</span>
                    <span className="font-mono text-xs text-blue-400 shrink-0">{r.presences}/{r.total_days}</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-slate-800 mt-1 overflow-hidden">
                    <div className="h-full rounded-full bg-gradient-to-r from-blue-500 to-cyan-400" style={{ width: `${(r.presences / maxPres) * 100}%` }} />
                  </div>
                </div>
                {r.absences.length > 0 && (
                  <span className="text-[10px] text-rose-400 font-mono shrink-0" title="Assenze">{r.absences.length} ass.</span>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Fair-Play */}
        <div className="ap-card p-5" data-testid="fairplay-section">
          <div className="flex items-center gap-2 mb-4">
            <Scale className="w-5 h-5 text-emerald-400" />
            <h3 className="font-display uppercase tracking-wide text-lg text-white">Classifica Fair-Play</h3>
          </div>
          <div className="space-y-2">
            {fairplay.map((r, i) => (
              <div key={r.player_id} data-testid={`fairplay-row-${r.player_id}`} className="rounded-xl bg-slate-950/50 border border-slate-800">
                <div className="flex items-center gap-3 px-3 py-2">
                  <span className="w-5 text-center font-mono text-xs text-slate-500">{i + 1}</span>
                  <PlayerAvatar id={r.player_id} name={r.name} size={30} ring={false} />
                  <span className="text-sm text-slate-200 truncate flex-1">{r.name}</span>
                  <span className={`font-mono font-black text-lg shrink-0 ${r.fairplay_points > 0 ? "text-emerald-400" : r.fairplay_points < 0 ? "text-rose-400" : "text-slate-500"}`}>
                    {r.fairplay_points > 0 ? "+" : ""}{r.fairplay_points}
                  </span>
                </div>
                {r.fairplay_log.length > 0 && (
                  <div className="px-3 pb-2 space-y-1">
                    {r.fairplay_log.map((l) => (
                      <div key={l.id} className="flex items-center gap-2 text-[11px] text-slate-500">
                        <span className={`font-mono font-bold ${l.delta >= 0 ? "text-emerald-400" : "text-rose-400"}`}>{l.delta > 0 ? "+" : ""}{l.delta}</span>
                        <span className="truncate flex-1">{l.reason || "—"}</span>
                        {isAdmin && (
                          <button data-testid={`fp-del-${l.id}`} onClick={() => removeEntry(l.id)} className="text-slate-600 hover:text-rose-400">
                            <Trash2 className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
